// @ts-nocheck
// The live tape: every trade derived from the playground, in time order — the
// single source the chart candles are built from (graph → tape → candles).
// Axiom-style columns: Time · Type · MC · Amount · SOL · Trader.

import { useRef, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

type Print = { ts: number; side: "buy" | "sell"; walletSol: number; tokenAmount: number; marketCapUsd: number; label?: string };

const compact = (n: number) => (n >= 1e9 ? `${(n / 1e9).toFixed(2)}B` : n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : n.toFixed(0));
const usd = (n: number) => (n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : `$${n.toFixed(0)}`);

export function LiveTape({ prints }: { prints: Print[] }) {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: prints.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 24, // Estimate height of .tape-tr (can adjust based on CSS)
    overscan: 10,
  });

  useEffect(() => {
    // Auto-scroll to bottom when new prints arrive if already at the bottom
    if (prints.length > 0 && parentRef.current) {
      const el = parentRef.current;
      const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
      if (isAtBottom) {
        rowVirtualizer.scrollToIndex(prints.length - 1);
      }
    }
  }, [prints.length, rowVirtualizer]);

  return (
    <div className="live-tape w-full h-full">
      <div className="live-tape-head">Live tape <b>{prints.length}</b> · built from the playground</div>
      <div className="tape-hr w-full">
        <span>Time</span><span>Type</span><span>MC</span><span>Amount</span><span>SOL</span><span>Trader</span>
      </div>
      <div 
        ref={parentRef} 
        className="tape-rows flex-1 min-h-0 overflow-y-auto custom-scrollbar w-full relative"
      >
        {prints.length === 0 && <div className="live-tape-empty">Add buys / sells in the playground — every trade shows here, in time order.</div>}
        
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const p = prints[virtualRow.index];
            return (
              <div
                key={virtualRow.index}
                className="tape-tr w-full"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <span className="tape-t">{(p.ts / 1000).toFixed(1)}s</span>
                <span className={`tape-type ${p.side}`}>{p.side === "buy" ? "Buy" : "Sell"}</span>
                <span className="tape-mc">{usd(p.marketCapUsd)}</span>
                <span className="tape-amt">{compact(p.tokenAmount)}</span>
                <span className={`tape-sol ${p.side}`}>{p.walletSol.toFixed(3)} SOL</span>
                <span className="tape-trader">{p.label ?? "—"}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
